// ============================================================================
// AUTH DOMAIN API CLIENT
// ============================================================================
// Domain-specific API for authentication, authorization, and session management
// ============================================================================

import { ApiClient } from '../../core/apiClient';
import { User, LoginCredentials, UserFormData } from '../../types';

class AuthApi extends ApiClient {
  constructor() {
    super({
      baseURL: '/api',
      enableLogging: import.meta.env.DEV,
    });
  }

  async login(credentials: LoginCredentials): Promise<{ token: string; refreshToken?: string; user: User }> {
    const response = await this.client.post('/auth/login', credentials);
    return response.data;
  }

  async logout(): Promise<void> {
    await this.client.post('/auth/logout');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }

  async loginWithTwoFactor(credentials: LoginCredentials): Promise<{ requiresTwoFactor: boolean; tempToken?: string; user?: User; token?: string; refreshToken?: string }> {
    const response = await this.client.post('/auth/login-2fa', credentials);
    return response.data;
  }

  async verifyTwoFactorToken(_email: string, token: string, tempToken?: string): Promise<{ success: boolean; message?: string; token?: string; refreshToken?: string; user?: User }> {
    const response = await this.client.post('/auth/verify-2fa', { tempToken, twoFactorToken: token });
    return response.data;
  }

  async resendTwoFactorToken(email: string): Promise<{ success: boolean; message?: string }> {
    const response = await this.client.post('/auth/resend-2fa', { email });
    return response.data;
  }

  async setupTwoFactor(): Promise<{ secret: string; qrCode: string; backupCodes: string[] }> {
    const response = await this.client.post('/2fa/setup/generate', {});
    return response.data.data;
  }

  async enableTwoFactor(token: string): Promise<void> {
    await this.client.post('/2fa/setup/enable', { token });
  }

  async verifyTwoFactor(userId: number, token?: string, backupCode?: string): Promise<void> {
    await this.client.post('/2fa/verify', { userId, token, backupCode });
  }

  async getTwoFactorStatus(): Promise<{ enabled: boolean }> {
    const response = await this.client.get('/2fa/status');
    return response.data.data;
  }

  async getTwoFactorSettings(): Promise<any> {
    const response = await this.client.get('/2fa/settings');
    return response.data.data;
  }

  async disableTwoFactor(): Promise<void> {
    await this.client.post('/2fa/disable', {});
  }

  async sendEmailCode(): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post('/2fa/send-email-code', {});
    return response.data;
  }

  async updateTwoFactorSettings(settings: { method?: string; rememberDevice?: boolean; rememberDeviceDays?: number }): Promise<{ success: boolean; message: string }> {
    const response = await this.client.put('/2fa/settings', settings);
    return response.data;
  }

  async regenerateBackupCodes(password: string): Promise<string[]> {
    const response = await this.client.post('/2fa/regenerate-backup-codes', { password });
    return response.data.data.backupCodes;
  }

  async updateUser(id: number, data: Partial<UserFormData>): Promise<void> {
    console.log('Client: Sending updateUser request', { id, data: JSON.stringify(data) });
    await this.client.put(`/auth/users/${id}`, data);
  }

  async getUsers(params?: { role?: string; company_id?: number }): Promise<User[]> {
    const response = await this.client.get('/users', { params });
    return response.data.data || response.data || [];
  }

  async getMe(): Promise<User> {
    const response = await this.client.get('/auth/me');
    return response.data.data || response.data;
  }

  async changePassword(data: { currentPassword: string; newPassword: string }): Promise<void> {
    await this.client.post('/auth/change-password', data);
  }

  async forgotPassword(email: string): Promise<{ success: boolean; message?: string }> {
    const response = await this.client.post('/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(token: string, newPassword: string): Promise<{ success: boolean; message?: string }> {
    const response = await this.client.post('/auth/reset-password', { token, password: newPassword });
    return response.data;
  }

  async uploadProfileImage(file: File): Promise<{ profile_picture_url: string }> {
    const formData = new FormData();
    formData.append('profile_picture', file);
 
    const response = await this.client.post('/auth/user/profile-picture', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
 
    return response.data.data || response.data;
  }

  async deleteProfileImage(): Promise<{ success: boolean; message: string }> {
    const response = await this.client.delete('/users/me/profile-image');
    return response.data;
  }

  async getProfileImage(): Promise<{ profile_image: string | null }> {
    const response = await this.client.get('/users/me/profile-image');
    return response.data.data || response.data;
  }

  async getUserSessions(): Promise<any[]> {
    const response = await this.client.get('/auth/user/sessions');
    return response.data.data || response.data || [];
  }

  async revokeSession(sessionId: string): Promise<{ success: boolean; message?: string }> {
    const response = await this.client.delete(`/auth/user/sessions/${sessionId}`);
    return response.data;
  }

  async checkSessionValidity(): Promise<{ valid: boolean }> {
    try {
      const response = await this.client.get('/auth/session/valid');
      return { valid: response.data.valid || false };
    } catch (error) {
      return { valid: false };
    }
  }

  async refreshAuthToken(): Promise<{ success: boolean; token?: string; message?: string }> {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        return { success: false, message: 'No refresh token available' };
      }

      const response = await this.client.post('/auth/refresh', {
        refreshToken
      });
      return response.data;
    } catch (error) {
      return { success: false, message: 'Token refresh failed' };
    }
  }
}

export const authApi = new AuthApi();
export default authApi;
